import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import {
  FirebaseMessaging,
  type NotificationActionPerformedEvent,
  type TokenReceivedEvent,
} from '@capacitor-firebase/messaging';
import api from '../lib/api';
import {
  getProNotificationLink,
  notificationLinkInputFromPushData,
  type ProNotificationLinkInput,
} from '../utils/proNotificationLinks';
import { PUSH_AFTER_LOGIN_FLAG } from '../constants/pushNotifications';

const ANDROID_CHANNEL_ID = 'capitol_commons_alerts';

const NOTIFICATION_QUERY_KEYS = [
  'pro-notifications',
  'notifications-unread-count',
  ['widget', 'notifications'],
  'notifications',
] as const;

function invalidateNotificationQueries(queryClient: ReturnType<typeof useQueryClient>) {
  for (const key of NOTIFICATION_QUERY_KEYS) {
    queryClient.invalidateQueries([key]);
  }
}

async function ensureAndroidChannel() {
  if (Capacitor.getPlatform() !== 'android') return;
  try {
    await FirebaseMessaging.createChannel({
      id: ANDROID_CHANNEL_ID,
      name: 'Alerts',
      description: 'Workspace and calendar alerts',
      importance: 5,
      visibility: 1,
      vibration: true,
    });
  } catch {
    // Channel may already exist
  }
}

async function registerTokenWithBackend(token: string) {
  const platform = Capacitor.getPlatform();
  if (platform !== 'ios' && platform !== 'android') return;

  let deviceId: string | undefined;
  try {
    const info = await Device.getId();
    deviceId = info.identifier;
  } catch {
    deviceId = undefined;
  }

  await api.post('/notifications/push/register', {
    token,
    platform,
    deviceId,
  });
}

/**
 * Fetch the current FCM token and POST it to the backend.
 *
 * Exposed for callers (e.g. ProProfileNotificationsPage) that grant permission
 * outside the useProPushNotifications effect lifecycle. The hook will also
 * register the token automatically on next mount, but calling this directly
 * after permission grant ensures the token is delivered without waiting for
 * the next app launch.
 */
export async function registerCurrentPushToken(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  const { token } = await FirebaseMessaging.getToken();
  if (!token) return;
  await registerTokenWithBackend(token);
}

export async function unregisterPushToken(token: string | null) {
  if (!token || !Capacitor.isNativePlatform()) return;
  try {
    await api.delete('/notifications/push/register', { data: { token } });
  } catch {
    // Best-effort on logout
  }
}

/**
 * Fetch the current FCM token and deregister it from the backend. Used on
 * sign-out so the signed-out user's device stops receiving their notifications.
 * Time-boxed so logout never hangs on a slow/offline network.
 */
export async function unregisterCurrentPushToken(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { token } = await FirebaseMessaging.getToken();
    if (!token) return;
    await Promise.race([
      unregisterPushToken(token),
      new Promise<void>((resolve) => setTimeout(resolve, 2000)),
    ]);
  } catch {
    // Best-effort on logout
  }
}

export interface UsePushNotificationsOptions {
  /** Route used when a tapped notification has no specific deep link. */
  fallbackRoute: string;
  /** Resolve a tapped notification's payload to an in-app deep link (or null). */
  resolveLink: (input: ProNotificationLinkInput) => string | null;
}

/**
 * Registers for FCM push on native builds and refreshes in-app notification queries
 * when a push arrives. Server always inserts notifications rows before sending push.
 *
 * Permission is never auto-prompted on cold start with a restored session. The system
 * dialog is shown only after a fresh native sign-in (see handleLoginSuccess) or when the
 * user taps Enable on a profile notifications page.
 *
 * Generic across role surfaces (Pro + Citizen) — only the deep-link resolver and the
 * fallback route differ. Token registration with the backend is role-agnostic.
 *
 * Uses @capacitor-firebase/messaging so both Android and iOS deliver FCM registration
 * tokens to the backend (iOS exchanges its APNs token with Firebase under the hood).
 */
export function usePushNotifications(enabled: boolean, options: UsePushNotificationsOptions) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Callers pass module-level constants (a literal route + a module-scope
  // resolver fn), so these are stable and safe as effect deps.
  const { fallbackRoute, resolveLink } = options;

  useEffect(() => {
    if (!enabled || !Capacitor.isNativePlatform()) return undefined;

    let cancelled = false;

    const setup = async () => {
      await ensureAndroidChannel();

      const shouldRequestAfterLogin =
        sessionStorage.getItem(PUSH_AFTER_LOGIN_FLAG) === '1';
      if (shouldRequestAfterLogin) {
        sessionStorage.removeItem(PUSH_AFTER_LOGIN_FLAG);
      }

      let permStatus = await FirebaseMessaging.checkPermissions();
      if (shouldRequestAfterLogin && permStatus.receive === 'prompt') {
        permStatus = await FirebaseMessaging.requestPermissions();
      }
      if (permStatus.receive !== 'granted') return;
      if (cancelled) return;

      try {
        const { token } = await FirebaseMessaging.getToken();
        if (cancelled || !token) return;
        await registerTokenWithBackend(token);
      } catch (err) {
        // Backend unreachable or Firebase init failed; effect re-runs on next mount.
        console.error('Push token registration with backend failed:', err);
      }
    };

    setup().catch((err) => {
      console.error('Push setup failed:', err);
    });

    // Token refresh — FCM rotates tokens periodically and on app reinstall.
    const onTokenReceived = FirebaseMessaging.addListener(
      'tokenReceived',
      async (event: TokenReceivedEvent) => {
        try {
          await registerTokenWithBackend(event.token);
        } catch (err) {
          console.error('Push token refresh with backend failed:', err);
        }
      },
    );

    const onReceived = FirebaseMessaging.addListener(
      'notificationReceived',
      () => {
        invalidateNotificationQueries(queryClient);
      },
    );

    const onAction = FirebaseMessaging.addListener(
      'notificationActionPerformed',
      (event: NotificationActionPerformedEvent) => {
        invalidateNotificationQueries(queryClient);

        const data = (event.notification.data || {}) as Record<string, unknown>;
        const link = resolveLink(notificationLinkInputFromPushData(data));

        if (data.notificationId) {
          api.put(`/notifications/${data.notificationId}/read`).catch(() => {});
        }

        navigate(link || fallbackRoute);
      },
    );

    // Deregistration is handled in the sign-out handler, which calls
    // unregisterCurrentPushToken() BEFORE clearing the session (see handleSignOut).
    // We intentionally do NOT deregister from an `auth:cleared`/`authChange`
    // listener: those fire after clearAuthSession has already removed the token
    // (and on a 401 the token is invalid), so the DELETE could never authenticate.

    return () => {
      cancelled = true;
      onTokenReceived.then((h) => h.remove());
      onReceived.then((h) => h.remove());
      onAction.then((h) => h.remove());
    };
  }, [enabled, navigate, queryClient, fallbackRoute, resolveLink]);
}

/**
 * Pro convenience wrapper — preserves the original useProPushNotifications API
 * (deep links to /pro/*, falls back to /pro/notifications).
 */
export function useProPushNotifications(enabled: boolean) {
  usePushNotifications(enabled, {
    fallbackRoute: '/pro/notifications',
    resolveLink: getProNotificationLink,
  });
}
